import { useEffect } from 'react';

import { useElements } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import { type StripePaymentElementChangeEvent } from '@stripe/stripe-js';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Listens to the change events from the payment element.
 */
export const usePaymentElementChangeListener = (params: Params) => {
  const { stripe, elements, setDefaultPaymentMethod, setCanSubmit } = params;

  useEffect(() => {
    if (!stripe || !elements) return;

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) return;

    const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
      const isCard = event.value.type === 'card';
      const isComplete = event.complete;
      const paymentMethodId = event.value.payment_method?.id;

      // Customer selected a payment method, set it as the default.
      if (paymentMethodId) setDefaultPaymentMethod(paymentMethodId);

      // Customer filled out the form, allow submission.
      setCanSubmit(isCard && isComplete && !paymentMethodId);
    };

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, setDefaultPaymentMethod, setCanSubmit]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  stripe: Stripe | null;
  elements: ReturnType<typeof useElements>;
  setDefaultPaymentMethod: (paymentMethodId: string) => void;
  setCanSubmit: (canSubmit: boolean) => void;
};
